import { AxiosError, type AxiosRequestConfig, type AxiosResponse } from 'axios';

import collectiveApi from 'modules/common/collectiveApi';

/**
 * The request layer for every generated API client. Each generated hook calls this, so the shared
 * axios instance keeps applying: proactive token refresh, force-logout on 401 and Datadog RUM
 * reporting. A generated client with its own transport would skip all three.
 *
 * Shared rather than per-domain on purpose — it is transport, not domain logic, and a copy per
 * client is a copy that drifts. `orval.config.ts` points every contract's output at this file, or at
 * the domain's `<slug>ApiMutator.ts`, which delegates here and adds only request options.
 */
export const collectiveApiMutator = async <T>(config: AxiosRequestConfig, options?: AxiosRequestConfig): Promise<T> => {
    // `{ ...config, ...options }` alone would let a caller's `headers` REPLACE the generated
    // client's, dropping `Content-Type`. Orval omits `in: header` parameters entirely (the
    // payroll v1 writes all require `Idempotency-Key`), so this options bag is the only way to
    // send one — it has to add a header, not swap the set.
    const response = await collectiveApi({
        ...config,
        ...options,
        headers: { ...config.headers, ...options?.headers },
    });

    // collectiveApi resolves a 401 rather than rejecting, so legacy callers reading `.data` keep
    // working. Returning that here would hand react-query an error body as if it were data.
    if (response.status >= 400) {
        throw new AxiosError(
            `Request failed with status code ${response.status}`,
            String(response.status),
            response.config,
            (response as AxiosResponse).request,
            response
        );
    }

    return response.data as T;
};

/** Generated hooks type their `error` as this, so the API error envelope survives to the UI. */
export type ErrorType<Error> = AxiosError<Error>;
